import {
    Component,
    ChangeDetectionStrategy,
    Input,
    ViewChild,
    type Type,
    type ComponentRef,
    type SimpleChanges,
    type OnChanges,
    inject,
} from '@angular/core';
import { of } from 'rxjs';

import { SpHostDirective } from '@common/directives/host.directive';

import type { WebLegacyWidgetEntity, WebWidgetModel } from '@web/types';
import { type WebWidgetControlComponent } from '@web/types';

import { WebWidgetsService } from '@web/core/services/web-widgets.service';
import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { SpHostDirective as SpHostDirective_1 } from '@common/directives/host.directive';
import { widgetNameOf } from '@common/helpers/widget-envelope';

@Component({
    selector: 'web-control-resolver',
    template: '<ng-container spHost></ng-container>',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpHostDirective_1],
})
export class ControlResolverComponent implements OnChanges {
    @Input() public widget: WebLegacyWidgetEntity;
    @Input() public isSpecific: boolean = false;
    @Input() public isEduSite!: boolean;

    @ViewChild(SpHostDirective, { static: true }) public widgetHost: SpHostDirective;

    private readonly widgetsService: WebWidgetsService = inject(WebWidgetsService);
    private readonly widgetsFacade: WidgetsFacade = inject(WidgetsFacade);

    public ngOnChanges(changes: SimpleChanges) {
        const { widget } = changes;

        if (!changes || !widget || widget.previousValue?.id === widget.currentValue.id) {
            return;
        }

        this.loadComponent();
    }

    private loadComponent(): void {
        const component: Type<WebWidgetControlComponent> = this.widgetsService.getWidgetControlComponent(
            widgetNameOf(this.widget)
        );

        if (!component) {
            return;
        }

        const model: WebWidgetModel = this.widgetsService.getWidgetModel(widgetNameOf(this.widget));

        if (model?.signalForm) {
            const { viewContainerRef } = this.widgetHost;
            viewContainerRef.clear();

            const componentRef: ComponentRef<WebWidgetControlComponent> =
                viewContainerRef.createComponent<WebWidgetControlComponent>(component);

            componentRef.setInput('widget', this.widget);
            componentRef.setInput('isSpecific', this.isSpecific);
            componentRef.setInput('isEduSite', this.isEduSite);
            return;
        }

        const { viewContainerRef } = this.widgetHost;
        viewContainerRef.clear();

        const componentRef: ComponentRef<WebWidgetControlComponent> =
            viewContainerRef.createComponent<WebWidgetControlComponent>(component);

        const instance = componentRef.instance as any;
        instance.container$ = of({});
        instance.widget$ = this.widgetsFacade.widgetById$(this.widget.id);
        instance.widget = this.widget;
        instance.isSpecific = this.isSpecific;
        instance.isEduSite = this.isEduSite;
    }
}
